import type { InterviewSummaryView } from '@nest-recruit/shared';
import type { InterviewListFilter } from './api/interviews-api';

// Redesign Group 7 (Interview-detail mockup): Previous / Next and "9 of 10 this week" walk the
// list the interview was opened from. The list a row was clicked in rides along in history state
// (as the review card's origin does, Group 3), kept out of the address; the rows themselves are
// read from that list's cached query, so a change on the list shows in the count. Opened from
// anywhere else — a notification, the palette, a reload — there is no list and the arrows hide.

export interface InterviewListContext {
  /** The list's query, exactly as the page asked for it. */
  readonly filter: InterviewListFilter;
  /** "this week", "coming up" — read after "9 of 10". */
  readonly label: string;
  /** The calendar's days (`YYYY-MM-DD`, viewer's zone): its query fetches a day either side. */
  readonly days?: readonly string[];
}

declare module '@tanstack/react-router' {
  interface HistoryState {
    readonly interviewList?: InterviewListContext;
  }
}

export interface ListPosition {
  readonly index: number;
  readonly total: number;
  readonly previous: InterviewSummaryView | null;
  readonly next: InterviewSummaryView | null;
}

/** Where an interview sits in the list it was opened from, or null when it is not in it. */
export function listPosition(rows: readonly InterviewSummaryView[], interviewId: string): ListPosition | null {
  const index = rows.findIndex((row) => row.id === interviewId);
  if (index < 0) return null;
  return { index, total: rows.length, previous: rows[index - 1] ?? null, next: rows[index + 1] ?? null };
}

/**
 * The rows in the order the list showed them. The calendar shows its week by start time; the
 * other lists show the server's order.
 */
export function orderedRows(rows: readonly InterviewSummaryView[], context: InterviewListContext, dayOf: (iso: string) => string): readonly InterviewSummaryView[] {
  const { days } = context;
  if (!days) return rows;
  return rows
    .filter((row) => row.startsAt !== null && days.includes(dayOf(row.startsAt)))
    .sort((a, b) => (a.startsAt ?? '').localeCompare(b.startsAt ?? ''));
}
